import { isPresent } from 'ts-is-present';
import { getQueryClient } from '../../utils/queryClient';
import { medicalProfileFilesService } from '../../primary-record-types/apiTypes';
import { APITypes, Dynamo } from '../../primary-record-types';
import urls from '../../utils/urls';

export const longTermCacheStaleTime = 1000 * 60 * 10; // 10 minutes

class Builder {
    private _key: string[];
    static init() {
        return new Builder([]);
    }

    constructor(key: string[]) {
        this._key = key;
    }

    private _add(domain: string, value?: string) {
        this._key.push(domain);

        const maybeNormalizedEmptyString = value === '' ? '*' : value;
        if (maybeNormalizedEmptyString) {
            this._key.push(maybeNormalizedEmptyString);
        }

        return this;
    }

    private _normalizeMaybeArray = (value: string[] | string | undefined) => {
        if (Array.isArray(value)) {
            return [...value].sort().join(',');
        }

        return value;
    };

    public preferences() {
        return this._add('preferences');
    }

    public medicalProfileList() {
        return this._add('medicalProfileList');
    }

    public medicalProfile(medicalProfileId?: string) {
        return this._add('medicalProfile', medicalProfileId);
    }

    public folder(folderId?: string) {
        return this._add('folder', folderId);
    }

    public medicalProfileDetails() {
        return this._add('medicalProfileDetails');
    }

    public medicalProfileOwner() {
        return this._add('medicalProfileOwner');
    }

    public resources() {
        return this._add('resources');
    }

    public resourceCategory(resourceCategory?: string) {
        return this._add('resourceCategory', resourceCategory);
    }

    public sort(sort?: 'ascending' | 'descending') {
        return this._add('resourceCategorySort', sort);
    }

    public careTeamMember(careTeamMemberId?: string) {
        return this._add('careTeamMembers', careTeamMemberId);
    }

    public relatedCareTeamMember(relatedCareTeamMemberId?: string) {
        return this._add('relatedCareTeamMember', relatedCareTeamMemberId);
    }

    public auditLogs() {
        return this._add('auditLogs');
    }

    public auditLog(auditLogId?: string) {
        return this.auditLogs()._add('auditLog', auditLogId);
    }

    public auditLogTypes(auditLogTypes?: string[]) {
        return this.auditLogs()._add('auditLogTypes', this._normalizeMaybeArray(auditLogTypes));
    }

    public collaborator(collaboratorId?: string) {
        return this._add('collaborators', collaboratorId);
    }

    public collaboratorInvite(collaboratorInviteId?: string) {
        return this._add('collaboratorInvite', collaboratorInviteId);
    }

    public myInvites() {
        return this._add('myInvites');
    }

    public evaluatedPermissions() {
        return this._add('evaluatedPermissions');
    }

    public inboundTransferRequests() {
        return this._add('inboundTransferRequests');
    }

    public get key() {
        return this._key;
    }

    public contactDetails(contactDetailsId: string) {
        return this._add('contactDetails', contactDetailsId);
    }

    public idCard(idCardId?: string) {
        return this._add('idCard', idCardId);
    }

    public resourcesCount() {
        return this._add('resourcesCount');
    }

    public refreshProgress() {
        return this._add('refreshProgress');
    }

    public notes(noteId?: string) {
        return this._add('notes', noteId);
    }

    public userNotes() {
        return this._add('userNotes');
    }

    public profileNotes() {
        return this._add('profileNotes');
    }

    public careTeamMemberNotes(careTeamMemberId?: string) {
        return this._add('careTeamMemberNotes', careTeamMemberId);
    }

    public resourceNotes(resourceIds?: string[] | string) {
        return this._add('resourceNotes', this._normalizeMaybeArray(resourceIds));
    }

    public v2NotesEntity(isV2Note: boolean) {
        return this._add('v2NotesEntity', String(isV2Note));
    }

    public resourceCategoryNotes(category: string) {
        return this._add('resourceCategoryNotes', category);
    }

    public oneUpEndpoint(endpointId: string | number) {
        return this._add('oneUpEndpoint', String(endpointId));
    }

    public oneUpEndpointsSearch(search: string) {
        return this._add('oneUpEndpointsSearch', search);
    }

    public fastenCatalogSearch(search: string, apiMode?: APITypes.ehrIntegrationsService.FastenApiMode) {
        // Live and sandbox catalogs return different ids for the same search, so the mode is part of the key.
        return this._add('fastenCatalogSearch', search)._add('fastenApiMode', apiMode ?? 'live');
    }

    public products() {
        return this._add('products');
    }

    public profilePaymentStatus() {
        return this._add('profilePaymentStatus');
    }

    public registrationCodeValidation(registrationCode: string) {
        return this._add('registrationCodeValidation', registrationCode);
    }

    public myRegistrationCode() {
        return this._add('myRegistrationCode');
    }

    public profileConnectedHealthSystems() {
        return this._add('profileConnectedHealthSystems');
    }

    public transferRequests() {
        return this._add('transferRequests');
    }

    public transferRequest(transferRequestId: string) {
        return this._add('transferRequest', transferRequestId);
    }

    public resource(resourceId?: string) {
        return this._add('resource', resourceId);
    }

    public relatedResources(resourceIds?: string | string[]) {
        return this._add('relatedResources', this._normalizeMaybeArray(resourceIds));
    }

    public user() {
        return this._add('user');
    }

    public webProducts() {
        return this._add('webProducts');
    }

    public paymentStatus() {
        return this._add('paymentStatus');
    }

    public oneUpHealthEndpoints() {
        return this._add('oneUpEndpoint');
    }

    public oneUpHealthEndpoint(endpointId: number | string) {
        return this.oneUpHealthEndpoints()._add(String(endpointId));
    }

    public oneUpHealthEndpointIngestions() {
        return this._add('oneUpHealthEndpointIngestions');
    }

    public resourceReferenceCounts(resourceId: string) {
        return this._add('resourceReferenceCounts', resourceId);
    }

    public processingReports() {
        return this._add('processingReports');
    }

    public chatsList(profileId: string) {
        return this._add('chatsList', profileId);
    }

    public chat(chatId: string) {
        return this._add('chat', chatId);
    }

    public relatedResourceSuggestions() {
        return this._add('relatedResourceSuggestions');
    }

    public relatedResourceDiscoveries() {
        return this._add('relatedResourceDiscoveries');
    }

    public sharedReport(sharedReportId?: string) {
        return this._add('sharedReport', sharedReportId);
    }

    public publicSharedReport(shareCode: string) {
        return this._add('publicSharedReport', shareCode);
    }

    public medicalProfileFile(medicalProfileFileId?: string) {
        return this._add('medicalProfileFile', medicalProfileFileId);
    }

    public polling() {
        return this._add('polling');
    }

    public pageImage(page: number) {
        return this._add('pageImage', String(page));
    }

    public audio() {
        return this._add('audio');
    }

    public search(searchText: string, searchType: medicalProfileFilesService.SearchType) {
        return this._add('search', searchText)._add('type', searchType);
    }

    public vaccineSearch(searchText: string) {
        return this._add('vaccine-search', searchText);
    }

    public featureToggles() {
        return this._add('featureToggles');
    }

    public labDateBuckets() {
        return this._add('labDateBuckets');
    }

    public dateLabs(date: string) {
        return this._add('dateLabs', date);
    }

    public labHistory(labCode: string) {
        return this._add('labHistory', labCode);
    }

    public uniqueLabCodes() {
        return this._add('uniqueLabCodes');
    }

    public organization() {
        return this._add('organization');
    }

    public hasOrganizationLogo() {
        return this._add('hasOrganizationLogo');
    }

    public groups() {
        return this._add('groups');
    }

    public invitationDefinitions(invitationDefinitionId?: string) {
        return this._add('invitationDefinitions', invitationDefinitionId);
    }

    public organizationUserInvitations(invitationId?: string) {
        return this._add('organizationUserInvitations', invitationId);
    }

    public myOrganizationInvitations() {
        return this._add('myOrganizationInvitations');
    }

    public getOrganizationUsers(organizationUserId?: string) {
        return this._add('organizationUsers', organizationUserId);
    }

    public getOrganizationUserGroups(organizationGroupUserId?: string) {
        return this._add('organizationUserGroups', organizationGroupUserId);
    }

    public getOrganizationClientInvitations(organizationClientInvitationId?: string) {
        return this._add('organizationClientInvitations', organizationClientInvitationId);
    }

    public myOrganizationClientInvitations() {
        return this._add('myOrganizationClientInvitations');
    }

    public clientRecordGroups() {
        return this._add('clientRecordGroups');
    }

    public organizationClientRecords(organizationClientRecordId?: string) {
        return this._add('organizationClientRecords', organizationClientRecordId);
    }

    public bulkOrganizationClientRecords(clientRecordIds: string[]) {
        return this.organizationClientRecords(clientRecordIds.sort().join(','));
    }

    public clientRecordInvitation() {
        return this._add('clientRecordInvitation');
    }

    public clientRecordHistory(profileId: string) {
        return this._add('clientRecordHistoryProfileId', profileId);
    }

    public organizationUsage() {
        return this._add('organizationUsage');
    }

    public maxMedicalProfiles() {
        return this._add('maxMedicalProfiles');
    }

    public organizationMedicalProfileFolderId(clientMedicalProfileId: string) {
        return this._add('organizationMedicalProfileFolderId', clientMedicalProfileId);
    }

    public description() {
        return this._add('description');
    }

    public metricType(type?: Dynamo.MedicalProfileUsageMetricType) {
        return this._add('metricType', type);
    }

    public fdaSearch(searchKey: string) {
        return this._add('fdaSearch', searchKey);
    }

    public paymentIntegrationStatus() {
        return this._add('paymentIntegrationStatus');
    }

    public paymentIntegrationBranding(logoType?: 'icon' | 'logo') {
        return this._add('paymentIntegrationBranding', logoType);
    }

    public clientRecordBillingDetails() {
        return this._add('clientRecordBillingDetails');
    }

    public clientRecordsTableSettings() {
        return this._add('clientRecordsTableSettings');
    }

    public invoiceItems() {
        return this._add('invoiceItems');
    }

    // Single items get their own segment: _add drops an undefined id, so `invoiceItems(undefined)` would
    // otherwise be the list key, and the item query's queryFn would overwrite the list.
    public invoiceItem(invoiceItemId?: string) {
        return this.invoiceItems()._add('invoiceItem', invoiceItemId);
    }

    public invoices() {
        return this._add('invoices');
    }

    public invoice(invoiceId?: string) {
        return this.invoices()._add('invoice', invoiceId);
    }

    public canAddMedicalProfile() {
        return this._add('canAddMedicalProfile');
    }

    public collaboratorInvoices(invoiceId?: string) {
        return this._add('collaboratorInvoices', invoiceId);
    }

    public directive(directiveType: 'code-status' | 'poa') {
        return this._add('directive', directiveType);
    }

    public medicalProfileSearch(searchText: string) {
        return this._add('medical-profile-search', searchText);
    }

    public inAppNotifications() {
        return this._add('in-app-notifications');
    }

    public inAppNotificationCounts() {
        return this._add('in-app-notification-counts');
    }

    public clientInAppNotificationsCount() {
        return this._add('client-in-app-notifications-count');
    }

    public adminListedOrgs() {
        return this._add('admin-listed-orgs');
    }

    public visitInviteEmails() {
        return this._add('visitInviteEmails');
    }
}

export const getProfileVisitInviteEmailsCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).visitInviteEmails().key;

export const getAdminListedOrgsKey = () => Builder.init().adminListedOrgs().key;

export const getAllProfileInAppNotificationsCountKey = () =>
    Builder.init().medicalProfile().inAppNotifications().inAppNotificationCounts().key;

export const getProfileInAppNotificationKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).inAppNotifications().key;

export const getAuditLogCacheKey = (medicalProfileId: string, auditLogId?: string) =>
    Builder.init().medicalProfile(medicalProfileId).auditLog(auditLogId).key;

export const getAuditLogTypeQueryCacheKey = (medicalProfileId: string, auditLogTypes?: string[]) =>
    Builder.init().medicalProfile(medicalProfileId).auditLogTypes(auditLogTypes).key;

export const getRelatedCareTeamMemberCacheKey = (medicalProfileId: string, careTeamMemberId: string) =>
    Builder.init().medicalProfile(medicalProfileId).relatedCareTeamMember(careTeamMemberId).key;

export const getProfileCareTeamMembersCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).careTeamMember().key;

export const getCareTeamMemberCacheKey = (medicalProfileId: string, careTeamMemberId: string) =>
    Builder.init().medicalProfile(medicalProfileId).careTeamMember(careTeamMemberId).key;

export const getProfileCollaboratorsCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).collaborator().key;

export const getProfileCollaboratorCacheKey = (medicalProfileId: string, collaboratorId: string) =>
    Builder.init().medicalProfile(medicalProfileId).collaborator(collaboratorId).key;

export const getProfileCollaboratorInvoicesCacheKey = (medicalProfileId: string, collaboratorId: string) =>
    Builder.init().medicalProfile(medicalProfileId).collaborator(collaboratorId).collaboratorInvoices().key;

export const getProfileCollaboratorInvoiceCacheKey = (
    medicalProfileId: string,
    collaboratorId: string,
    invoiceId: string,
) => Builder.init().medicalProfile(medicalProfileId).collaborator(collaboratorId).collaboratorInvoices(invoiceId).key;

export const getProfileCollaboratorInvitesCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).collaboratorInvite().key;

export const getProfileCollaboratorInviteCacheKey = (medicalProfileId: string, collaboratorInviteId: string) =>
    Builder.init().medicalProfile(medicalProfileId).collaboratorInvite(collaboratorInviteId).key;

export const getCareTeamMemberCollaboratorsCacheKey = (medicalProfileId: string, careTeamMemberId: string) =>
    Builder.init().medicalProfile(medicalProfileId).careTeamMember(careTeamMemberId).collaborator().key;

const getProfileResourcesRootCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().key;

const getAllProfileRelatedResourcesCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().relatedResources().key;

export const getResourceReferenceCountsCacheKey = (medicalProfileId: string, resourceId: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().resourceReferenceCounts(resourceId).key;

export const getResourceRelatedCareTeamMembersCacheKey = (medicalProfileId: string, resourceIds: string | string[]) =>
    Builder.init().medicalProfile(medicalProfileId).resources().relatedResources(resourceIds).careTeamMember().key;

export const getMyInvitesCacheKey = () => Builder.init().myInvites().key;

export const getEvaluatedPermissionsCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).evaluatedPermissions().key;

export const getInboundTransferRequestsCacheKey = () => Builder.init().inboundTransferRequests().key;

export const getTransferRequestCacheKey = (medicalProfileId: string, transferRequestId: string) =>
    Builder.init().medicalProfile(medicalProfileId).transferRequest(transferRequestId).key;

export const getProfilesRootCacheKey = () => Builder.init().medicalProfile();

export const getMedicalProfilesCacheKey = () => getProfilesRootCacheKey().medicalProfileList().key;

export const getMedicalProfileDetailsCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileDetails().key;

export const getMedicalProfileOwnerCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileOwner().key;

export const getMedicalProfileContactDetailsCacheKey = (medicalProfileId: string, contactDetailsId: string) =>
    Builder.init().medicalProfile(medicalProfileId).contactDetails(contactDetailsId).key;

export const getMedicalProfileIdCardsCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).idCard().key;

export const getResourcesCountCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().resourcesCount().key;

const getRootMedicalProfileEndpointCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).oneUpHealthEndpoints().key;

export const getOneUpHealthEndpointDetailsCacheKey = (medicalProfileId: string, endpointId: string | number) =>
    Builder.init().medicalProfile(medicalProfileId).oneUpHealthEndpoint(endpointId).key;

export const getOneUpHealthEndpointIngestionsCacheKey = (medicalProfileId: string, endpointId: string | number) =>
    Builder.init().medicalProfile(medicalProfileId).oneUpHealthEndpoint(endpointId).oneUpHealthEndpointIngestions().key;

export const getPublicSharedReportCacheKey = (shareCode: string) => Builder.init().publicSharedReport(shareCode).key;

export const getSharedReportCacheKey = (medicalProfileId: string, sharedReportId: string) =>
    Builder.init().medicalProfile(medicalProfileId).sharedReport(sharedReportId).key;

export const getSharedReportsCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).sharedReport().key;

export const getMedicalProfileFileCacheKey = (medicalProfileId: string, medicalProfileFileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileFile(medicalProfileFileId).key;

export const getMedicalProfileFilesCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileFile().key;

export const getMedicalProfileFoldersCacheKey = (medicalProfileId: string, folderId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileFile().folder(folderId).key;

export const getMedicalProfileFilesFileCacheKey = (medicalProfileId: string, medicalProfileFileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileFile(medicalProfileFileId).key;

export const getMedicalProfileFilesFilePollingCacheKey = (medicalProfileId: string, medicalProfileFileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileFile(medicalProfileFileId).polling().key;

export const getVaccineSearchCacheKey = (searchText: string) => Builder.init().vaccineSearch(searchText).key;

export const getFDASearchCacheKey = (request: APITypes.medicalDataService.SearchFDADrugsRequest) => {
    let searchString = '';
    if (request.type === 'exact') {
        searchString = `exact-brandName:${request.brandName}-activeIngredients:${(request.activeIngredients || [])
            .sort()
            .join()}-dosageForm:${request.dosageForm}-route:${request.route}`;
    } else {
        searchString = `partial-${request.searchString}`;
    }

    return Builder.init().fdaSearch(searchString).key;
};

export const getMedicalProfileFileSearchCacheKey = (
    medicalProfileId: string,
    searchText: string,
    searchType: medicalProfileFilesService.SearchType,
) => Builder.init().medicalProfile(medicalProfileId).search(searchText, searchType).key;

export const getMedicalProfileFilePageImageCacheKey = (
    medicalProfileId: string,
    medicalProfileFileId: string,
    page: number,
) => Builder.init().medicalProfile(medicalProfileId).medicalProfileFile(medicalProfileFileId).pageImage(page).key;

export const getMedicalProfileFilePageAudioCacheKey = (medicalProfileId: string, medicalProfileFileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).medicalProfileFile(medicalProfileFileId).audio().key;

export const getProfileResourcesForCategoryCacheKey = (
    medicalProfileId: string,
    resourceCategory: string,
    sort?: 'ascending' | 'descending',
) => Builder.init().medicalProfile(medicalProfileId).resources().resourceCategory(resourceCategory).sort(sort).key;

export const getProfileLabDateBucketsCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().labDateBuckets().key;

export const getDateLabsCacheKey = (medicalProfileId: string, date: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().dateLabs(date).key;

export const getUniqueLabCodesCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().uniqueLabCodes().key;

export const getLabHistoryCacheKey = (medicalProfileId: string, labCode: string) =>
    Builder.init().medicalProfile(medicalProfileId).resources().labHistory(labCode).key;

export const getProfileResourceForCategoryCacheKey = (
    medicalProfileId: string,
    resourceCategory: string,
    resourceId: string,
) =>
    Builder.init().medicalProfile(medicalProfileId).resources().resourceCategory(resourceCategory).resource(resourceId)
        .key;

export const getRelatedResourcesCacheKey = (medicalProfileId: string, resourceCategory: string, resourceId: string) =>
    Builder.init()
        .medicalProfile(medicalProfileId)
        .resources()
        .resourceCategory(resourceCategory)
        .resource(resourceId)
        .relatedResources().key;

export const getRelatedResourcesSuggestionsCacheKey = (
    medicalProfileId: string,
    resourceCategory: string,
    resourceId: string,
) =>
    Builder.init()
        .medicalProfile(medicalProfileId)
        .resources()
        .resourceCategory(resourceCategory)
        .resource(resourceId)
        .relatedResources()
        .relatedResourceSuggestions().key;

export const getRelatedResourcesDiscoveriesCacheKey = (
    medicalProfileId: string,
    resourceCategory: string,
    resourceId: string,
) =>
    Builder.init()
        .medicalProfile(medicalProfileId)
        .resources()
        .resourceCategory(resourceCategory)
        .resource(resourceId)
        .relatedResources()
        .relatedResourceDiscoveries().key;

export const getProfileRefreshProgressCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).refreshProgress().key;

export const getUserNotesCacheKey = () => Builder.init().notes().userNotes().key;

const getMedicalProfileNotesRootCacheKey = (medicalProfileId: string) =>
    Builder.init().medicalProfile(medicalProfileId).notes();

export const getProfileNotesCacheKey = (medicalProfileId: string) =>
    getMedicalProfileNotesRootCacheKey(medicalProfileId).profileNotes().key;

export const getCareTeamMemberNotesCacheKey = (medicalProfileId: string, careTeamMemberId: string) =>
    getMedicalProfileNotesRootCacheKey(medicalProfileId).careTeamMemberNotes(careTeamMemberId).key;

export const getResourceNotesCacheKey = (
    medicalProfileId: string,
    resourceIds: string[] | string,
    isV2Entity: boolean,
) => getMedicalProfileNotesRootCacheKey(medicalProfileId).resourceNotes(resourceIds).v2NotesEntity(isV2Entity).key;

export const getResourceCategoryNotesCacheKey = (medicalProfileId: string, resourceCategory: string) =>
    getMedicalProfileNotesRootCacheKey(medicalProfileId).resourceCategoryNotes(resourceCategory).key;

export const getNoteCacheKey = (noteId: string) => {
    return Builder.init().notes(noteId).key;
};

export const getAllUserNotesCacheKey = () => {
    return Builder.init().notes().key;
};

export const getOneUpEndpointCacheKey = (endpointId: string | number) => {
    return Builder.init().oneUpEndpoint(endpointId).key;
};

export const getOneUpEndpointsSearchCacheKey = (search: string) => {
    return Builder.init().oneUpEndpointsSearch(search).key;
};

export const getFastenCatalogSearchCacheKey = (
    search: string,
    apiMode?: APITypes.ehrIntegrationsService.FastenApiMode,
) => {
    return Builder.init().fastenCatalogSearch(search, apiMode).key;
};

export const getOrganizationEntityAuditLogsQueryCacheKey = () => Builder.init().organization().auditLogs().key;

export const getProductsCacheKey = () => {
    return Builder.init().products().key;
};

export const getProfilePaymentStatusCacheKey = () => {
    return Builder.init().profilePaymentStatus().key;
};

export const getProfileConnectedHealthSystemsCacheKey = (medicalProfileId: string) => {
    return Builder.init().medicalProfile(medicalProfileId).profileConnectedHealthSystems().key;
};

export const getProfileTransferRequestsCacheKey = (medicalProfileId?: string) => {
    return Builder.init().medicalProfile(medicalProfileId).transferRequests().key;
};

export const getUserCacheKey = () => {
    return Builder.init().user().key;
};

export const getRegistrationCodeValidationCacheKey = (registrationCode: string) => {
    return Builder.init().registrationCodeValidation(registrationCode).key;
};

export const getWebProductsCacheKey = () => {
    return Builder.init().webProducts().key;
};

export const getPaymentStatusCacheKey = () => {
    return Builder.init().paymentStatus().key;
};

export const getProfileIdPaymentStatusCacheKey = (medicalProfileId: string) => {
    return Builder.init().profilePaymentStatus().medicalProfile(medicalProfileId).key;
};

export const getCanAddMedicalProfileCacheKey = () => {
    return Builder.init().canAddMedicalProfile().key;
};

export const getMyRegistrationCodeCacheKey = () => {
    return Builder.init().myRegistrationCode().key;
};

export const getProfileProcessingReportsKey = (medicalProfileId: string) => {
    return Builder.init().medicalProfile(medicalProfileId).processingReports().key;
};

export const getCareTeamMemberResourcesCacheKey = (
    medicalProfileId: string,
    careTeamMemberId: string,
    resourceCategory: string,
) => {
    return Builder.init()
        .medicalProfile(medicalProfileId)
        .careTeamMember(careTeamMemberId)
        .resourceCategory(resourceCategory).key;
};

export const getChatsListCacheKey = (profileId: string) => {
    return Builder.init().chatsList(profileId).key;
};

export const getMedicalProfileChatKey = (medicalProfileId: string, chatId: string) => {
    return Builder.init().medicalProfile(medicalProfileId).chat(chatId).key;
};

export const getFeatureTogglesKey = () => {
    return Builder.init().featureToggles().key;
};

export const getOrganizationKey = () => {
    return Builder.init().organization().key;
};

export const getHasOrganizationLogoKey = () => {
    return Builder.init().organization().hasOrganizationLogo().key;
};

export const getOrganizationGroupsCacheKey = () => {
    return Builder.init().organization().groups().key;
};

export const getOrganizationInvitationDefinitionsCacheKey = () => {
    return Builder.init().organization().invitationDefinitions().key;
};

export const getOrganizationInvitationDefinitionCacheKey = (invitationDefinitionId: string) => {
    return Builder.init().organization().invitationDefinitions(invitationDefinitionId).key;
};

export const getOrganizationUserInvitationsCacheKey = () => {
    return Builder.init().organization().organizationUserInvitations().key;
};

export const getOrganizationUserInvitationCacheKey = (invitationId: string) => {
    return Builder.init().organization().organizationUserInvitations(invitationId).key;
};

export const getMyOrganizationInvitationsCacheKey = () => {
    return Builder.init().myOrganizationInvitations().key;
};

export const getOrganizationUsersCacheKey = () => {
    return Builder.init().organization().getOrganizationUsers().key;
};

export const getOrganizationUserCacheKey = (organizationUserId: string) => {
    return Builder.init().organization().getOrganizationUsers(organizationUserId).key;
};

export const getOrganizationUserGroupsCacheKey = (organizationUserId: string) => {
    return Builder.init().organization().getOrganizationUsers(organizationUserId).getOrganizationUserGroups().key;
};

export const getOrganizationClientInvitationsCacheKey = () => {
    return Builder.init().organization().getOrganizationClientInvitations().key;
};

export const getOrganizationClientInvitationCacheKey = (organizationClientInvitationId: string) => {
    return Builder.init().organization().getOrganizationClientInvitations(organizationClientInvitationId).key;
};

export const getMyOrganizationClientInvitationsCacheKey = () => {
    return Builder.init().myOrganizationClientInvitations().key;
};

export const getOrganizationMedicalProfilesCacheKey = (medicalProfileId?: string) => {
    return Builder.init().organization().medicalProfile(medicalProfileId).key;
};

export const getOrganizationClientProfileNotificationCountCacheKey = () => {
    return Builder.init().organization().clientInAppNotificationsCount().key;
};

export const getClientRecordGroupsCacheKey = (clientRecordId: string) => {
    return Builder.init().organization().organizationClientRecords(clientRecordId).clientRecordGroups().key;
};

export const getOrganizationClientRecordsCacheKey = () => {
    return Builder.init().organization().organizationClientRecords().key;
};

export const getOrganizationClientRecordsBulkCacheKey = (clientRecordIds: string[]) => {
    return Builder.init().organization().bulkOrganizationClientRecords(clientRecordIds).key;
};

export const getOrganizationClientRecordHistoryCacheKey = (profileId: string) => {
    return Builder.init().organization().organizationClientRecords().clientRecordHistory(profileId).key;
};

export const getOrganizationClientRecordCacheKey = (organizationClientRecordId: string) => {
    return Builder.init().organization().organizationClientRecords(organizationClientRecordId).key;
};

export const getOrganizationClientRecordBillingDetailsCacheKey = (organizationClientRecordId: string) => {
    return Builder.init()
        .organization()
        .organizationClientRecords(organizationClientRecordId)
        .clientRecordBillingDetails().key;
};

export const getOrganizationClientRecordsTableSettingsCacheKey = () => {
    return Builder.init().organization().clientRecordsTableSettings().key;
};

export const getClientRecordInvitationCacheKey = (organizationClientRecordId: string) => {
    return Builder.init().organization().organizationClientRecords(organizationClientRecordId).clientRecordInvitation()
        .key;
};

export const getOrganizationNotesCacheKey = () => {
    return Builder.init().organization().notes().key;
};

export const getOrganizationClientRecordNotesCacheKey = (organizationClientRecordId: string) => {
    return Builder.init().organization().organizationClientRecords(organizationClientRecordId).notes().key;
};

export const getOrganizationCareTeamMembersCacheKey = () => {
    return Builder.init().organization().careTeamMember().key;
};

export const getOrganizationCareTeamMemberCacheKey = (careTeamMemberId: string) => {
    return Builder.init().organization().careTeamMember(careTeamMemberId).key;
};

export const getOrganizationClientRecordAuditLogsCacheKey = (organizationClientRecordId: string) => {
    return Builder.init().organization().organizationClientRecords(organizationClientRecordId).auditLogs().key;
};

export const getOrganizationClientRecordInvoiceItemsCacheKey = (organizationClientRecordId: string) => {
    return Builder.init().organization().organizationClientRecords(organizationClientRecordId).invoiceItems().key;
};

export const getOrganizationClientRecordInvoiceItemCacheKey = (
    organizationClientRecordId: string,
    invoiceItemId: string,
) => {
    return Builder.init()
        .organization()
        .organizationClientRecords(organizationClientRecordId)
        .invoiceItem(invoiceItemId).key;
};

export const getOrganizationClientRecordInvoicesCacheKey = (organizationClientRecordId: string) => {
    return Builder.init().organization().organizationClientRecords(organizationClientRecordId).invoices().key;
};

export const getOrganizationClientRecordInvoiceCacheKey = (organizationClientRecordId: string, invoiceId: string) => {
    return Builder.init().organization().organizationClientRecords(organizationClientRecordId).invoice(invoiceId).key;
};

export const getOrganizationClientRecordInvoiceInvoiceItemsCacheKey = (
    organizationClientRecordId: string,
    invoiceId: string,
) => {
    return Builder.init()
        .organization()
        .organizationClientRecords(organizationClientRecordId)
        .invoice(invoiceId)
        .invoiceItems().key;
};

export const getOrganizationUsageCacheKey = () => {
    return Builder.init().organization().organizationUsage().key;
};

export const getMaxMedicalProfilesCacheKey = () => {
    return Builder.init().organization().organizationClientRecords().maxMedicalProfiles().key;
};

export const getOrganizationMedicalProfileFolderIdCacheKey = (clientMedicalProfileId: string) => {
    return Builder.init()
        .organization()
        .organizationClientRecords()
        .organizationMedicalProfileFolderId(clientMedicalProfileId).key;
};

export const getOrganizationPaymentIntegrationStatusCacheKey = () => {
    return Builder.init().organization().paymentIntegrationStatus().key;
};

const getBaseOrganizationPaymentIntegrationBrandingCacheKey = () => {
    return Builder.init().organization().paymentIntegrationBranding().key;
};

export const getOrganizationPaymentIntegrationBrandingImageCacheKey = (logoType: 'icon' | 'logo') => {
    return Builder.init().organization().paymentIntegrationBranding(logoType).key;
};

export const getUserPreferencesCacheKey = () => {
    return Builder.init().preferences().key;
};

export const getMedicalProfileResourceDescriptionForCategoryCacheKey = (
    medicalProfileId: string,
    resourceCategory: string,
    resourceId: string,
) => {
    return Builder.init()
        .medicalProfile(medicalProfileId)
        .resources()
        .resourceCategory(resourceCategory)
        .resource(resourceId)
        .description().key;
};

const getProfileMetricRootCacheKey = (medicalProfileId: string) => {
    return Builder.init().medicalProfile(medicalProfileId).metricType().key;
};

export const getUsageMetricTypeCacheKey = (medicalProfileId: string, type: Dynamo.MedicalProfileUsageMetricType) => {
    return Builder.init().medicalProfile(medicalProfileId).metricType(type).key;
};

export const getMedicalProfileDirectiveCacheKey = (medicalProfileId: string, directiveType: 'code-status' | 'poa') => {
    return Builder.init().medicalProfile(medicalProfileId).directive(directiveType).key;
};

export const getMedicalProfileSearchCacheKey = (medicalProfileId: string, searchText: string) => {
    return Builder.init().medicalProfile(medicalProfileId).medicalProfileSearch(searchText).key;
};

const UNIVERSAL_DELAY = 700;

type GetResetHandlerArgs<TParams> = {
    getResetCacheKeys: (params: TParams) => string[][];
    resetDelay?: number;
    removeCache?: boolean;
};

const getResetHandlers = <TParams>(args: GetResetHandlerArgs<TParams>) => {
    const maybeDelay = (fn: () => void) => () => {
        if (args.resetDelay !== 0) {
            setTimeout(fn, args.resetDelay || UNIVERSAL_DELAY);
        } else {
            fn();
        }
    };

    return {
        resetCache: (params: TParams) => {
            const queryClient = getQueryClient();
            const resetCache = maybeDelay(() => {
                args.getResetCacheKeys(params).forEach((key) => {
                    if (args.removeCache) {
                        queryClient.resetQueries({ queryKey: key });
                    } else {
                        queryClient.invalidateQueries({ queryKey: key });
                    }
                });
            });

            return resetCache;
        },
        resetCacheWithDeferredParams: (): ((params: TParams) => void) => {
            const queryClient = getQueryClient();
            const resetCache = (params: TParams) => {
                maybeDelay(() => {
                    args.getResetCacheKeys(params).forEach((key) => {
                        if (args.removeCache) {
                            queryClient.resetQueries({ queryKey: key });
                        } else {
                            queryClient.invalidateQueries({ queryKey: key });
                        }
                    });
                })();
            };

            return resetCache;
        },
    };
};

export const onPatientMismatchConfirmedUpdate = getResetHandlers<{
    medicalProfileId: string;
    endpointId: string | number;
}>({
    getResetCacheKeys: ({ medicalProfileId, endpointId }) => [
        getOneUpHealthEndpointDetailsCacheKey(medicalProfileId, endpointId),
    ],
});

export const onRefreshCapabilityDiscovery = getResetHandlers<{
    medicalProfileId: string;
    endpointId: string | number;
}>({
    getResetCacheKeys: ({ medicalProfileId, endpointId }) => [
        getOneUpHealthEndpointDetailsCacheKey(medicalProfileId, endpointId),
        getRootMedicalProfileEndpointCacheKey(medicalProfileId),
        getProfileConnectedHealthSystemsCacheKey(medicalProfileId),
    ],
});

/**
 * Resets metadata associated with a medical profile connections
 */
export const onResetProfileConnectionMetadata = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getRootMedicalProfileEndpointCacheKey(medicalProfileId),
        getUsageMetricTypeCacheKey(medicalProfileId, 'file-upload'),
        getUsageMetricTypeCacheKey(medicalProfileId, 'portal-connect'),
    ],
});

/**
 * Related care team member updated or deleted
 */
export const onRelatedCareTeamMemberUpdate = getResetHandlers<{
    medicalProfileId: string;
    careTeamMemberId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId, careTeamMemberId }) => [
        getRelatedCareTeamMemberCacheKey(medicalProfileId, careTeamMemberId),
    ],
});

/**
 * Medical profile care team member is added, updated, or deleted
 */
export const onCareTeamUpdate = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getProfileCareTeamMembersCacheKey(medicalProfileId),
        getProfileResourcesRootCacheKey(medicalProfileId),
    ],
});

export const onCopyOrgCareTeamMemberSuccess = getResetHandlers<{ profileIds: string[] }>({
    getResetCacheKeys: ({ profileIds }) => [
        ...profileIds.map((medicalProfileId) => getProfileCareTeamMembersCacheKey(medicalProfileId)),
        ...profileIds.map((medicalProfileId) => getProfileResourcesRootCacheKey(medicalProfileId)),
    ],
});

/**
 * Emergency contact is added
 */
export const onEmergencyContactAdd = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileCareTeamMembersCacheKey(medicalProfileId)],
});

/**
 * On resource update.
 *
 * This clears all cached resource data for a profile to ensure reference counts, related resources, etc are in sync
 */
export const onResourceUpdate = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getProfileResourcesRootCacheKey(medicalProfileId),
        getAuditLogTypeQueryCacheKey(medicalProfileId),
    ],
    resetDelay: 700,
});

/**
 * On a bulk resource edit or delete operation
 */
export const onResourceBulkUpdate = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileResourcesRootCacheKey(medicalProfileId)],
});

/**
 * On a bulk resource create operation
 */
export const onResourceBulkCreate = onResourceBulkUpdate;

/**
 * On a resource -> care team member relationship is updated
 */
export const onResourceRelatedCareTeamMemberUpdates = getResetHandlers<{
    medicalProfileId: string;
    resourceCategory: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getAllProfileRelatedResourcesCacheKey(medicalProfileId),
        Builder.init().medicalProfile(medicalProfileId).careTeamMember().key,
        getResourceRelatedCareTeamMembersCacheKey(medicalProfileId, ''),
    ],
});

/**
 * On a medical profile collaborator is added, updated, or deleted
 */
export const onCollaboratorUpdate = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getProfileCollaboratorsCacheKey(medicalProfileId),
        getProfileCollaboratorInvitesCacheKey(medicalProfileId),
        getProfileMetricRootCacheKey(medicalProfileId),
    ],
});

export const onCollaboratorInviteUpdate = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getProfileCollaboratorInvitesCacheKey(medicalProfileId),
        getProfileCollaboratorsCacheKey(medicalProfileId),
        getProfilePaymentStatusCacheKey(),
        getMedicalProfilesCacheKey(),
        getMyInvitesCacheKey(),
        getEvaluatedPermissionsCacheKey(medicalProfileId),
    ],
    resetDelay: 0,
    removeCache: true,
});

/**
 * Refresh the medical data ingest progress
 */
export const refreshProfileDataRefreshProgress = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileRefreshProgressCacheKey(medicalProfileId)],
});

/**
 * On and addition or deletion of medical profiles
 */
export const onMedicalProfileUpdate = getResetHandlers<{}>({
    getResetCacheKeys: () => [getMedicalProfilesCacheKey(), getProfilePaymentStatusCacheKey(), getUserCacheKey()],
});

/**
 * On a medical note update.  Resets all notes for a profile and all notes for a user as well as profile files
 */
export const onNotesUpdate = getResetHandlers<{ medicalProfileId?: string; organizationClientRecordId?: string }>({
    getResetCacheKeys: ({ medicalProfileId, organizationClientRecordId }) =>
        [
            medicalProfileId ? getMedicalProfileNotesRootCacheKey(medicalProfileId).key : null,
            medicalProfileId ? getProfileResourcesRootCacheKey(medicalProfileId) : null,
            medicalProfileId ? getMedicalProfileFilesCacheKey(medicalProfileId) : null,
            medicalProfileId ? getUsageMetricTypeCacheKey(medicalProfileId, 'file-upload') : null,
            medicalProfileId ? getUsageMetricTypeCacheKey(medicalProfileId, 'audio-file-upload') : null,
            organizationClientRecordId ? getOrganizationClientRecordNotesCacheKey(organizationClientRecordId) : null,
            getOrganizationNotesCacheKey(),
            getAllUserNotesCacheKey(),
        ].filter(isPresent),
    resetDelay: 600,
    removeCache: true,
});

/**
 * When a comment is added to a note
 */
export const onNoteComment = getResetHandlers<{ medicalProfileId?: string; organizationClientRecordId?: string }>({
    getResetCacheKeys: ({ medicalProfileId, organizationClientRecordId }) =>
        [
            medicalProfileId ? getMedicalProfileNotesRootCacheKey(medicalProfileId).key : null,
            medicalProfileId ? getProfileResourcesRootCacheKey(medicalProfileId) : null,
            organizationClientRecordId ? getOrganizationClientRecordNotesCacheKey(organizationClientRecordId) : null,
            getAllUserNotesCacheKey(),
            medicalProfileId ? getProfileInAppNotificationKey(medicalProfileId) : null,
            getAllProfileInAppNotificationsCountKey(),
            getOrganizationClientProfileNotificationCountCacheKey(),
        ].filter(isPresent),
});

/**
 * When the client-record copies of profile notes may have changed as a side effect: a profile note the organization
 * copies was created, edited, deleted, or copied. The copies are found through eventually consistent indexes, so the
 * reset waits for them like the other note resets.
 */
export const onClientRecordNoteCopiesChanged = getResetHandlers<NonNullable<unknown>>({
    getResetCacheKeys: () => [getOrganizationClientRecordsCacheKey()],
});

/**
 * When a note's links to client-record copies may have changed as a side effect (a professional's comment created
 * the organization's copy). The links are found through an eventually consistent index, so the detail refetch waits.
 */
export const onNoteLinksChanged = getResetHandlers<{ noteId: string }>({
    getResetCacheKeys: ({ noteId }) => [getNoteCacheKey(noteId)],
});

export const onOrgNoteCopiedToProfiles = getResetHandlers<{ profileIds: string[] }>({
    getResetCacheKeys: ({ profileIds }) =>
        profileIds.map((medicalProfileId) => getMedicalProfileNotesRootCacheKey(medicalProfileId).key),
});

/**
 * When a transfer request is accepted.  Updates collaborators, profile list, payment status, transfer requests
 */
export const onTransferRequestResponse = getResetHandlers<{ medicalProfileId?: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getProfileTransferRequestsCacheKey(medicalProfileId),
        getProfileCollaboratorsCacheKey(medicalProfileId!),
        getMedicalProfilesCacheKey(),
        getProfilePaymentStatusCacheKey(),
    ],
});

/**
 * When a transfer request is created or revoked.  Updates the transfer requests
 */
export const onTransferRequestUpdated = getResetHandlers<{ medicalProfileId?: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileTransferRequestsCacheKey(medicalProfileId)],
});

/**
 * When profile id cards are added or deleted
 */
export const onIdCardUpdate = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getMedicalProfileIdCardsCacheKey(medicalProfileId)],
    removeCache: true,
});

/**
 * Force reset product list
 */
export const onResetProducts = getResetHandlers<{}>({
    getResetCacheKeys: () => [getProductsCacheKey()],
});

/**
 * Reset payment status
 */
export const onResetPaymentStatus = getResetHandlers<{}>({
    getResetCacheKeys: () => [
        getProfilePaymentStatusCacheKey(),
        getPaymentStatusCacheKey(),
        getMyRegistrationCodeCacheKey(),
        Builder.init().medicalProfile().key, // Reset all medical profiles specifically for metric tracking
    ],
});

/**
 * on shared reports update
 */
export const onSharedReportUpdate = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getSharedReportsCacheKey(medicalProfileId)],
});

/**
 * Reset payment status
 */
export const onProfileContactDetailsUpdate = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [getMedicalProfileDetailsCacheKey(medicalProfileId)],
});

/**
 * Reset connected health systems
 */
export const onResetConnectedHealthSystems = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileConnectedHealthSystemsCacheKey(medicalProfileId)],
});

/**
 * After initiating an automatic portal refresh, reset the connected health systems
 * There is a delay due to the relatively long async process of refreshing the portal
 */
export const onInitiatedAutomaticPortalRefresh = {
    portalMetadataResetHandler: getResetHandlers<{ medicalProfileId: string }>({
        getResetCacheKeys: ({ medicalProfileId }) => [getProfileConnectedHealthSystemsCacheKey(medicalProfileId)],
        resetDelay: 60 * 1000,
    }),
    usageHandler: getResetHandlers<{ medicalProfileId: string }>({
        getResetCacheKeys: ({ medicalProfileId }) => [getUsageMetricTypeCacheKey(medicalProfileId, 'portal-connect')],
    }),
};

export const onTogglePortalAutomaticRefresh = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileConnectedHealthSystemsCacheKey(medicalProfileId)],
});

export const onProcessReportInitiated = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileProcessingReportsKey(medicalProfileId)],
});

export const onChatMessage = getResetHandlers<{ medicalProfileId: string; chatId: string }>({
    getResetCacheKeys: ({ medicalProfileId, chatId }) => [getMedicalProfileChatKey(medicalProfileId, chatId)],
});

export const onChatCrud = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getChatsListCacheKey(medicalProfileId),
        getUsageMetricTypeCacheKey(medicalProfileId, 'chat-message'),
    ],
});

export const onRefreshChat = onChatMessage;

export const onAuditLogUpdate = getResetHandlers<{ medicalProfileId: string; auditLogId?: string }>({
    getResetCacheKeys: ({ medicalProfileId, auditLogId }) => [
        getAuditLogTypeQueryCacheKey(medicalProfileId),
        getAuditLogCacheKey(medicalProfileId, auditLogId),
        getProfileInAppNotificationKey(medicalProfileId),
        getAllProfileInAppNotificationsCountKey(),
        getOrganizationClientProfileNotificationCountCacheKey(),
    ],
});

export const onAuditLogComment = getResetHandlers<{ medicalProfileId: string; auditLogId: string }>({
    getResetCacheKeys: ({ medicalProfileId, auditLogId }) => [
        getAuditLogTypeQueryCacheKey(medicalProfileId),
        getAuditLogCacheKey(medicalProfileId, auditLogId),
        getProfileInAppNotificationKey(medicalProfileId),
        getAllProfileInAppNotificationsCountKey(),
        getOrganizationClientProfileNotificationCountCacheKey(),
        getProfileInAppNotificationKey(medicalProfileId),
    ],
});

export const onUpdateMedicalProfileFile = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getMedicalProfileFilesCacheKey(medicalProfileId),
        getUsageMetricTypeCacheKey(medicalProfileId, 'file-upload'),
    ],
});

export const onReprocessComplete = getResetHandlers<{ medicalProfileId: string; medicalProfileFileId: string }>({
    getResetCacheKeys: ({ medicalProfileId, medicalProfileFileId }) => [
        getMedicalProfileFileCacheKey(medicalProfileId, medicalProfileFileId),
    ],
});

export const onDeletePortalConnection = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileConnectedHealthSystemsCacheKey(medicalProfileId)],
});

export const onAuditLogMessage = getResetHandlers<{ medicalProfileIds: string[] }>({
    getResetCacheKeys: ({ medicalProfileIds }) => {
        const resetKeys = medicalProfileIds
            .map((id) => [getAuditLogTypeQueryCacheKey(id), getProfileInAppNotificationKey(id)])
            .flat();

        return resetKeys;
    },
});

export const onUpdateOrganization = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationKey()],
});

export const onUpdateOrganizationPaymentIntegrationBranding = getResetHandlers<{ organizationId: string }>({
    getResetCacheKeys: ({ organizationId }) => [
        getBaseOrganizationPaymentIntegrationBrandingCacheKey(),
        [
            urls.api.organizations.paymentIntegration.brandingImage(organizationId, 'icon'),
            urls.api.organizations.paymentIntegration.brandingImage(organizationId, 'logo'),
        ],
    ],
});

/**
 * Anytime a group is created, updated, or deleted
 */
export const onUpdateOrganizationGroups = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationGroupsCacheKey(), getOrganizationMedicalProfilesCacheKey()],
    resetDelay: 0,
});

export const onUpdateOrganizationInvitationDefinitions = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationInvitationDefinitionsCacheKey()],
    resetDelay: 0,
});

export const onUpdateOrganizationUserInvitations = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationUserInvitationsCacheKey()],
    resetDelay: 0,
});

export const onUpdateMyOrganizationInvitation = getResetHandlers<{}>({
    getResetCacheKeys: () => [getMyOrganizationInvitationsCacheKey()],
    resetDelay: 0,
});

export const onUpdateOrganizationUsers = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationUsersCacheKey()],
    resetDelay: 0,
});

export const onUpdateOrganizationUser = getResetHandlers<{ organizationUserId: string }>({
    getResetCacheKeys: ({ organizationUserId }) => [getOrganizationUserCacheKey(organizationUserId)],
    resetDelay: 0,
});

export const onUpdateOrganizationClientInvitations = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationClientInvitationsCacheKey(), getOrganizationClientRecordsCacheKey()],
    resetDelay: 0,
});

export const onRespondToOrganizationClientInvitation = getResetHandlers<{}>({
    getResetCacheKeys: () => [getMyOrganizationClientInvitationsCacheKey(), getProfilesRootCacheKey().key],
    removeCache: true,
    resetDelay: 0,
});

export const onUpdateOrganizationMedicalProfiles = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationMedicalProfilesCacheKey(), getOrganizationClientRecordsCacheKey()],
    resetDelay: 0,
});

export const onDeleteOrganizationMedicalProfile = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationMedicalProfilesCacheKey(), getOrganizationClientRecordsCacheKey()],
    removeCache: true,
    resetDelay: 0,
});

export const onUpdateOrganizationClientRecordGroups = getResetHandlers<{ clientRecordId: string }>({
    getResetCacheKeys: ({ clientRecordId }) => [
        getClientRecordGroupsCacheKey(clientRecordId),
        getOrganizationClientRecordsCacheKey(),
    ],
    resetDelay: 0,
});

export const onUBulkUpdateOrganizationClientRecordGroups = getResetHandlers<{ clientRecordIds: string[] }>({
    getResetCacheKeys: ({ clientRecordIds }) => [
        ...clientRecordIds.map((_) => getClientRecordGroupsCacheKey(_)),
        getOrganizationClientRecordsCacheKey(),
    ],
    resetDelay: 0,
});

export const onUpdateOrganizationClientRecords = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationClientRecordsCacheKey()],
    resetDelay: 0,
});

export const onClientInvitationsUpdate = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationClientInvitationsCacheKey(), getOrganizationClientRecordsCacheKey()],
    resetDelay: 0,
});

export const onInviteClientRecord = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationClientInvitationsCacheKey(), getOrganizationClientRecordsCacheKey()],
    resetDelay: 0,
});

export const onUpdateOrganizationCareTeamMembers = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationCareTeamMembersCacheKey()],
    resetDelay: 0,
});

export const onCreateOrganization = onUpdateOrganization;

export const onPersistFilesToClientProfiles = getResetHandlers<{ clientProfileIds: string[] }>({
    getResetCacheKeys: ({ clientProfileIds }) => [
        ...clientProfileIds.map((id) => getOrganizationMedicalProfilesCacheKey(id)),
        ...clientProfileIds.map((id) => getMedicalProfileFilesCacheKey(id)),
        ...clientProfileIds.map((id) => getOrganizationMedicalProfileFolderIdCacheKey(id)),
    ],
    removeCache: true,
});

export const onPersistVisitsToClientProfiles = getResetHandlers<{ clientProfileIds: string[] }>({
    getResetCacheKeys: ({ clientProfileIds }) => [
        ...clientProfileIds.map((id) => getOrganizationMedicalProfilesCacheKey(id)),
        ...clientProfileIds.map((id) => getProfileResourcesRootCacheKey(id)),
    ],
    removeCache: true,
});

export const onPreferencesUpdate = getResetHandlers<{}>({
    getResetCacheKeys: () => [getUserPreferencesCacheKey()],
    resetDelay: 0,
    removeCache: true,
});

export const onChatMessageSent = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [getUsageMetricTypeCacheKey(medicalProfileId, 'chat-message')],
});

export const onClientRecordBillingDetailsUpdate = getResetHandlers<{ organizationClientRecordId: string }>({
    getResetCacheKeys: ({ organizationClientRecordId }) => [
        getOrganizationClientRecordBillingDetailsCacheKey(organizationClientRecordId),
    ],
});

export const onClientRecordInvoiceItemsUpdate = getResetHandlers<{ organizationClientRecordId: string }>({
    getResetCacheKeys: ({ organizationClientRecordId }) => [
        getOrganizationClientRecordInvoiceItemsCacheKey(organizationClientRecordId),
        getOrganizationClientRecordInvoicesCacheKey(organizationClientRecordId),
    ],
});

export const onClientRecordInvoicesUpdate = getResetHandlers<{ organizationClientRecordId: string }>({
    getResetCacheKeys: ({ organizationClientRecordId }) => [
        getOrganizationClientRecordInvoiceItemsCacheKey(organizationClientRecordId),
        getOrganizationClientRecordInvoicesCacheKey(organizationClientRecordId),
    ],
});

export const onPaymentStatusOrProfileCountUpdate = getResetHandlers<{}>({
    getResetCacheKeys: () => [getCanAddMedicalProfileCacheKey()],
});

export const onSetupTestConnectedAccount = getResetHandlers<{}>({
    getResetCacheKeys: () => [getOrganizationKey()],
});

export const onProfileDirectiveUpdate = getResetHandlers<{
    medicalProfileId: string;
    directiveType: 'code-status' | 'poa';
}>({
    getResetCacheKeys: ({ medicalProfileId, directiveType }) => [
        getMedicalProfileDirectiveCacheKey(medicalProfileId, directiveType),
    ],
});

export const onToggleInAppNotificationReadStatus = getResetHandlers<{ medicalProfileId: string }>({
    getResetCacheKeys: ({ medicalProfileId }) => [
        getProfileInAppNotificationKey(medicalProfileId),
        getAllProfileInAppNotificationsCountKey(),
        getOrganizationClientProfileNotificationCountCacheKey(),
    ],
    removeCache: true,
});

export const onUpdateOrgPaidStatus = getResetHandlers<{}>({
    getResetCacheKeys: () => [getAdminListedOrgsKey()],
});

export const onUpdateProfileVisitInviteEmails = getResetHandlers<{
    medicalProfileId: string;
}>({
    getResetCacheKeys: ({ medicalProfileId }) => [getProfileVisitInviteEmailsCacheKey(medicalProfileId)],
    resetDelay: 0,
});
